import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { validateForm, loginSchema } from '../../utils/validation';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Label } from '../../components/Label';
import { Checkbox } from '../../components/Checkbox';

import { initiateGoogleSso } from '../../utils/sso';
import { InstallPWA } from '../../components/InstallPWA';

export function Login() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, isAuthenticated, pendingRequest, clearPendingRequest } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGoogleLogin = () => {
    setSsoLoading(true);
    try {
      initiateGoogleSso();
    } catch (err: any) {
      toast.error('Failed to initiate Google sign in');
      setSsoLoading(false);
    }
  };

  const update = (field: 'email' | 'password', value: string) => {
    if (field === 'email') setEmail(value);
    else setPassword(value);
    if (validationErrors[field]) setValidationErrors((current) => ({ ...current, [field]: undefined as never }));
    if (error || submitError) {
      clearError();
      setSubmitError(null);
    }
    if (pendingRequest) {
      clearPendingRequest();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearError();
    setSubmitError(null);
    const validation = validateForm(loginSchema, { email, password, rememberMe });
    if (!validation.success) {
      setValidationErrors(validation.errors || {});
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res?.status === 'pending_approval') {
        return;
      }
      toast.success('Login successful! Welcome back.');
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else navigate('/dashboard');
    } catch (caught: any) {
      setSubmitError(caught?.response?.data?.message || caught?.message || 'Unable to sign in');
    } finally {
      setSubmitting(false);
    }
  };

  const busy = isLoading || submitting || ssoLoading;

  if (pendingRequest) {
    return (
      <section className="w-full rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8 space-y-6">
        <header className="text-center space-y-2">
          <div className="size-12 rounded-full bg-warning/10 text-warning flex items-center justify-center mx-auto border border-warning/20">
            <span className="text-xl font-semibold">!</span>
          </div>
          <h1 className="font-display text-page-title">Access Request Pending</h1>
          <p className="text-xs font-mono text-muted-foreground">{pendingRequest.email}</p>
        </header>

        <div className="rounded-lg border border-border bg-secondary/40 p-4 text-xs text-muted-foreground space-y-2 leading-relaxed">
          <p className="font-medium text-foreground">Awaiting Administrator Approval</p>
          <p>{pendingRequest.message}</p>
          <p className="text-xs pt-1 text-muted-foreground border-t border-border/40">
            Once an administrator reviews your request, assigns your department, role, and initial task, your account will be activated and you will be able to sign in.
          </p>
        </div>

        <Button
          type="button"
          fullWidth
          variant="secondary"
          onClick={() => {
            clearPendingRequest();
            clearError();
            setSubmitError(null);
          }}
        >
          Sign in with another account
        </Button>
      </section>
    );
  }

  return (
    <section className="w-full rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8">
      <header className="mb-6">
        <h1 className="font-display text-page-title">Welcome back</h1>
        <p className="mt-1.5 text-sm text-text-tertiary">Sign in to continue to TeamOne.</p>
      </header>

      {(error || submitError) && <div role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error || submitError}</div>}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field id="email" label="Email address" type="email" value={email} onChange={(value) => update('email', value)} error={validationErrors.email} autoComplete="username" />
        <div className="space-y-1.5">
          <Label htmlFor="password" required>Password</Label>
          <div className="relative">
            <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => update('password', event.target.value)} invalid={Boolean(validationErrors.password)} aria-describedby={validationErrors.password ? 'password-error' : undefined} autoComplete="current-password" className="pr-10" />
            <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)} className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric-blue/50">
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {validationErrors.password && <p id="password-error" role="alert" className="text-xs text-destructive">{validationErrors.password}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <label htmlFor="rememberMe" className="flex cursor-pointer items-center gap-2 text-sm text-text-tertiary">
            <Checkbox id="rememberMe" name="rememberMe" checked={rememberMe} onCheckedChange={(value) => setRememberMe(value === true)} />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-sm font-semibold text-electric-blue hover:underline">Forgot password?</Link>
        </div>

        <Button type="submit" fullWidth isLoading={busy && !ssoLoading}>{busy && !ssoLoading ? 'Signing in...' : 'Sign in'}</Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>

      {/* Google SSO Button */}
      <Button
        type="button"
        variant="outline"
        fullWidth
        onClick={handleGoogleLogin}
        isLoading={ssoLoading}
        className="flex items-center justify-center gap-2.5 h-10 border-border bg-card hover:bg-secondary/40 text-foreground transition-all shadow-xs"
      >
        <svg className="size-4" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
          />
        </svg>
        <span className="font-medium text-sm">Sign in with Google</span>
      </Button>

      {/* <p className="mt-3 text-center text-xs text-muted-foreground">Sign in with Google is limited to <span className="font-semibold text-foreground">@trustybytes.in</span> accounts.</p> */}

      <div className="mt-6 flex flex-col items-center gap-2 border-t border-border/40 pt-5">
        <p className="text-xs text-muted-foreground">Get TeamOne as an app for faster access</p>
        <InstallPWA />
      </div>
    </section>
  );
}

function Field({ id, label, type = 'text', value, onChange, error, autoComplete }: { id: string; label: string; type?: string; value: string; onChange: (value: string) => void; error?: string; autoComplete?: string }) {
  const errorId = `${id}-error`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required>{label}</Label>
      <Input id={id} name={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete} invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} />
      {error && <p id={errorId} role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
